import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fileUrl, formatPrice, specDisplay } from "@/lib/api";
import { useCompare } from "@/context/CompareContext";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/layout/Layout";
import { Loading, Empty, ErrorBox } from "@/components/common/States";
import { ConditionBadge, Stars, StateBadge, VerifiedTick } from "@/components/common/Badges";
import SEO from "@/components/common/SEO";

const Unknown = () => <span className="italic text-zinc-500">Bilinmiyor</span>;

function Row({ label, cols, render, diff }) {
  return (
    <tr className={`border-t border-black/[0.08] ${diff ? "bg-[#0071e3]/[0.04]" : ""}`}>
      <th className="sticky left-0 z-10 w-48 bg-white p-3 text-left text-xs font-normal text-zinc-500">{label}</th>
      {cols.map((c) => <td key={c.type + c.id} className="min-w-[200px] p-3 font-mono text-[13px]">{render(c) ?? <Unknown />}</td>)}
    </tr>
  );
}

const Section = ({ title, span }) => (
  <tr><td colSpan={span} className="bg-black/[0.025] px-3 py-2 font-display text-sm font-semibold text-[#0077ed]">{title}</td></tr>
);

export default function Compare() {
  const { items, remove, clear } = useCompare();
  const { user } = useAuth();
  const key = items.map((i) => `${i.type}:${i.id}`).join(",");
  const { data, isLoading, error } = useQuery({
    queryKey: ["compare", key], enabled: items.length >= 2,
    queryFn: async () => (await api.post("/compare", { items: items.map(({ type, id }) => ({ type, id })) })).data,
  });
  const save = async () => {
    try { await api.post("/comparisons", { items: items.map(({ type, id }) => ({ type, id })), name: "Karşılaştırmam" }); toast.success("Karşılaştırma kaydedildi"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (items.length < 2)
    return <Page><h1 className="font-display text-4xl font-semibold">Karşılaştır</h1><div className="mt-8"><Empty title="Karşılaştırmak için en az 2 ürün ekleyin" hint="Katalog veya ilan kartlarındaki 'Karşılaştır' butonunu kullanın. Aktif ilanı olmayan katalog ürünleri de eklenebilir.">
      <Button asChild className="bg-[#0071e3] text-white"><Link to="/katalog">Kataloğa git</Link></Button></Empty></div></Page>;

  const cols = data?.columns || [];
  const span = cols.length + 1;
  const hasListing = cols.some((c) => c.type === "listing");
  return (
    <Page>
      <SEO
        title="FPV Parça Karşılaştırma"
        description="Seçtiğiniz FPV parçalarını ve ilanlarını teknik özelliklerine, ağırlık, voltaj ve bağlantı protokollerine göre yan yana karşılaştırın."
        canonical="https://createfpv.com/karsilastir"
      />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><p className="eyebrow">{data?.same_model ? "Aynı modelin farklı ilanları" : "Teknik karşılaştırma"}</p><h1 className="font-display text-4xl font-semibold">Karşılaştır</h1></div>
        <div className="flex gap-2">
          {user && <Button variant="outline" className="bg-transparent" onClick={save} data-testid="save-compare-button">Kaydet</Button>}
          <Button variant="outline" className="bg-transparent" onClick={clear} data-testid="clear-compare-button">Temizle</Button>
        </div>
      </div>
      <div className="mt-8">
        {isLoading && <Loading />}
        {error && <ErrorBox message={errMsg(error)} />}
        {data && (
          <div className="overflow-x-auto panel" data-testid="compare-table">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white p-3" />
                  {cols.map((c) => (
                    <th key={c.type + c.id} className="p-3 text-left align-top" data-testid={`compare-col-${c.id}`}>
                      <div className="flex justify-between gap-2">
                        <span className="rounded bg-black/[0.05] px-1.5 text-[10px] uppercase">{c.type === "product" ? "Katalog" : "İlan"}</span>
                        <button onClick={() => remove(c.type, c.id)} data-testid={`compare-remove-${c.id}`}><X className="h-4 w-4 text-zinc-500" /></button>
                      </div>
                      {c.image && <img src={fileUrl(c.image)} alt={c.title || "Karşılaştırılan parça"} loading="lazy" decoding="async" className="mt-2 h-24 w-full rounded object-cover" />}
                      <Link to={c.type === "product" ? `/katalog/${c.id}` : `/ilan/${c.id}`} className="mt-2 block font-display font-semibold hover:text-[#0077ed]">{c.title}</Link>
                      {c.product_title && <p className="text-xs font-normal text-zinc-500">{c.product_title}</p>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <Section title="Ortak ürün puanı (model bazlı)" span={span} />
                <Row label="Ürün puanı" cols={cols} render={(c) => c.rating ? (c.rating.count ? <span className="flex items-center gap-2"><Stars value={c.rating.average} size={3} />{c.rating.average} ({c.rating.count})</span> : "Yorum yok") : null} />
                <Row label="Son yorumlar" cols={cols} render={(c) => c.recent_reviews?.length ? <ul className="font-sans text-xs">{c.recent_reviews.map((r, i) => <li key={i}>★{r.rating} {r.title}</li>)}</ul> : null} />
                <Section title={data.same_model ? "Ortak teknik özellikler (katalog)" : "Teknik özellikler"} span={span} />
                {data.rows.map((r) => {
                  const vals = cols.map((c) => JSON.stringify((data.same_model ? c.catalog_specs : c.specs)?.[r.key] ?? null));
                  return <Row key={r.key} label={r.label} cols={cols} diff={new Set(vals).size > 1}
                    render={(c) => specDisplay(r, (data.same_model ? c.catalog_specs : c.specs)?.[r.key])} />;
                })}
                {data.same_model && (
                  <>
                    <Section title="İlana özel teknik farklar (satıcı beyanı)" span={span} />
                    <Row label="Değiştirilen özellikler" cols={cols} render={(c) => c.overridden_keys?.length
                      ? <ul className="text-xs">{c.overridden_keys.map((k) => { const r = data.rows.find((x) => x.key === k); return <li key={k}>{r?.label}: {specDisplay(r, c.specs[k]) ?? "Bilinmiyor"}</li>; })}</ul> : "Katalogla aynı"} />
                  </>
                )}
                {hasListing && (
                  <>
                    <Section title="İlan ve satıcı bilgileri" span={span} />
                    <Row label="Fiyat" cols={cols} render={(c) => c.listing && <span className="text-[#0071e3] font-semibold">{formatPrice(c.listing.price, c.listing.currency)}</span>} />
                    <Row label="Durum / kondisyon" cols={cols} render={(c) => c.listing && <span className="flex gap-1"><StateBadge state={c.listing.item_state} /><ConditionBadge code={c.listing.condition_class} /></span>} />
                    <Row label="Konum" cols={cols} render={(c) => c.listing && `${c.listing.district}, ${c.listing.province}`} />
                    <Row label="Satıcı" cols={cols} render={(c) => c.listing && <Link className="font-sans hover:text-[#0077ed]" to={`/satici/${c.listing.seller.id}`}>{c.listing.seller.full_name} <VerifiedTick show={c.listing.seller.is_verified_seller} /></Link>} />
                    <Row label="İletişim" cols={cols} render={(c) => c.listing && <Button asChild size="sm" className="bg-[#0071e3] text-white"><Link to={`/ilan/${c.id}`} data-testid={`compare-contact-${c.id}`}>Satıcıya ulaş</Link></Button>} />
                  </>
                )}
                <Row label="Aktif ilan" cols={cols} render={(c) => c.type === "product" ? (c.active_listings ? <Link className="text-[#0077ed]" to={`/katalog/${c.id}`}>{c.active_listings} ilan</Link> : "Aktif ilan yok") : null} />
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Page>
  );
}
